import Head from 'next/head'
import Script from 'next/script'
import Image from 'next/image'
import Link from 'next/link'
import { useState } from 'react'
import { useRouter } from 'next/router'
import { AnimatePresence } from 'framer-motion'
import { s3Static } from '../../../utils/constants'
import { contentDate, reviewedIso } from '../../../utils/article-date-time'
import { getRequestJSON } from '../../../api/index/location-api'
import { airconCostCalculation, hydroWashServicing } from '../../../js/aircon-cost'
import { RequestProvider } from '../../../context/RequestContext'
import { openModal } from '../../../service/modal-service'
import { Services } from '../../../components/index/service/Services'
import { HydroWashAirconBooking } from '@/index/service-category/aircon-service/HydroWashAirconBooking'
import { AirconItemSelection } from '@/index/service-category/aircon-service/AirconItemSelection'
import { AirconScope } from '@/index/service-category/aircon-service/AirconScope'
import { AirconWhyUs } from '@/index/service-category/aircon-service/AirconWhyUs'
import { AirconCustomerReviews } from '@/index/service-category/aircon-service/AirconCustomerReviews'
import { AirconBrandsWeSupport } from '@/index/service-category/aircon-service/AirconBrandsWeSupport'
import { AirconQuickLinks } from '@/index/service-category/aircon-service/AirconQuickLinks'
import { Faq } from '@/commons/Faq'
import { BlogsList } from '../../../components/blogs/BlogList'
import { blogsMaster } from '../../../utils/sg-blogs-data'
import { getWpNewPostsByCategory, getWpPostsByCategory } from '../../../lib/service'
import { getWpHvacServices, getWpNewHvacServices, setWpHvacServices, setWpNewHvacServices } from '../../../service/wp-blogs-store'
import { getTestimonials } from 'api/user/profile-api'
import { getAcTestimonials, setAcTestimonials } from 'service/testimonial-store'
import { buildAirconServiceGraph } from 'utils/aircon-page-schema'
import { buildAcHydroWashFaqs } from 'utils/faq'

// Chemical Hydro Wash (Singapore only), added in the Oct 2026 price review.
// Every price on this page comes from js/aircon-cost (the pricing profile the
// backend mirrors), so the copy, the tables, the FAQs and the schema agree.
//
// When this page's content last actually changed. Bump it by hand when you
// edit the copy - not for layout/UX changes. Feeds dateModified and
// article:modified_time (see reviewedIso in utils/article-date-time).
const CONTENT_REVIEWED = '2026-10-23'
const DATE_PUBLISHED = '2026-10-23T09:00:00+08:00'

const PAGE_PATH = '/sg/aircon-servicing-singapore/aircon-chemical-hydro-wash'
const PAGE_URL = `https://www.stringssg.com${PAGE_PATH}`
const HERO_IMAGE = 'blog/living/aircon/removing-chemical-from-aircon-with-jet-spray.jpg'

const STEPS = [
    { title: 'Inspection and preparation', desc: 'The technician checks the unit and covers the wall and floor underneath.' },
    { title: 'Partial dismantling', desc: 'The front panel, filters, blower wheel and drain tray are removed. The fan coil stays on the wall.' },
    { title: 'Chemical treatment', desc: 'A cleaning chemical is applied to the evaporator coil and blower to loosen dirt, grease and mould.' },
    { title: 'High-pressure water-jet rinse', desc: 'The coil, blower wheel and drain tray are rinsed with a water jet, which flushes out what the chemical has loosened.' },
    { title: 'Drainage check', desc: 'The drain tray and drainage pipe are checked so the rinse water and condensate can flow freely.' },
    { title: 'Reassembly and testing', desc: 'The parts go back in, and the unit is run and tested for airflow and cooling.' },
]

const SIGNS = [
    'A musty or sour smell when the aircon starts',
    'Black specks blowing out of the vents',
    'Weak airflow even after a general service',
    'A blower wheel you can see is coated in dust',
    'No deep clean in the last 6 to 12 months',
]

export async function getStaticProps() {
    const countryName = "Singapore"
    const currencyCd = "SGD"

    const allRequestCategorySettings = await getRequestJSON(countryName)
    const requestCategorySettings = allRequestCategorySettings.filter(category => category.requestType === "Aircon Services")

    const testimonials = getAcTestimonials().length > 0 ? getAcTestimonials() : await getTestimonials("Aircon Services", 0, 6)
    setAcTestimonials(testimonials)

    const modifiedDate = reviewedIso(CONTENT_REVIEWED)
    const { year } = contentDate(CONTENT_REVIEWED)
    const airconBlogs = blogsMaster['hvac-services'].blogArray
    const wpPosts = getWpHvacServices().length > 0 ? getWpHvacServices() : await getWpPostsByCategory(6, "hvac-services")
    setWpHvacServices(wpPosts)
    const wpNewPosts = getWpNewHvacServices().length > 0 ? getWpNewHvacServices() : await getWpNewPostsByCategory(6, "hvac-services")
    setWpNewHvacServices(wpNewPosts)

    let standardServicing = []
    let annualServicing = []
    let chemicalServicing = []
    let overhaulServicing = []
    airconCostCalculation(currencyCd, annualServicing, standardServicing, chemicalServicing, overhaulServicing)
    const hydroServicing = hydroWashServicing(currencyCd)

    return {
        props: {
            requestCategorySettings,
            testimonials,
            modifiedDate,
            year,
            airconBlogs,
            wpPosts,
            wpNewPosts,
            currencyCd,
            hydroServicing,
            wash1: chemicalServicing[0].rate,
            overhaul1: overhaulServicing[0].rate,
        },
        revalidate: 259200, // In seconds
    }
}

export default function Home(props) {
    const router = useRouter()
    const {
        requestCategorySettings, testimonials, modifiedDate, year, airconBlogs, wpPosts, wpNewPosts,
        currencyCd, hydroServicing, wash1, overhaul1,
    } = props
    const requestType = 'Aircon Services'

    const [ requestForm, setRequestForm ] = useState(router.query.requestForm)
    const [ isQuickBook, setIsQuickBook ] = useState(false)
    const [ isCustomBook, setIsCustomBook ] = useState(false)
    const [ isShowAirconItemSelection, setIsShowAirconItemSelection ] = useState(false)
    const [ isShowJobScope, setIsShowJobScope ] = useState(false)
    const [ scopeServiceType, setScopeServiceType ] = useState("chemicalHydroWash")
    const [ scopeUnitType, setScopeUnitType ] = useState("")

    const showJobScope = (serviceType, unitType) => {
        setScopeServiceType(serviceType)
        setScopeUnitType(unitType)
        openModal(setIsShowJobScope)
    }

    const money = (rate) => `$${rate}`
    const hydro1 = money(hydroServicing[0].rate)
    const hydro3 = money(hydroServicing[2].rate)
    const hydro5 = money(hydroServicing[4].rate)

    const pageTitle = `Aircon Chemical Hydro Wash Singapore | From ${hydro1}`
    const pageDescription = `Aircon chemical hydro wash (hydro jet wash) in Singapore from ${hydro1} per unit: blower wheel and drain tray removed, coil and blower water-jet rinsed, unit stays on the wall. Transport included, 60-day warranty.`
    const pageImage = s3Static + HERO_IMAGE

    const faqs = buildAcHydroWashFaqs({ hydro1, hydro3, wash1: money(wash1), overhaul1: money(overhaul1) })

    const comparison = [
        { label: 'What comes off', wash: 'Front panel and filters', hydro: 'Front panel, filters, blower wheel and drain tray', overhaul: 'The whole fan coil unit' },
        { label: 'How it is cleaned', wash: 'Chemical on the coil, rinsed', hydro: 'Chemical on coil and blower, high-pressure water jet', overhaul: 'Every part cleaned separately' },
        { label: 'Unit taken off the wall', wash: 'No', hydro: 'No', overhaul: 'Yes' },
        { label: 'Best for', wash: 'Weak airflow, light musty smell', hydro: 'Musty smell, black specks, dirty blower', overhaul: 'Poor cooling after a service, leaks, long unused' },
        { label: 'From (1 unit)', wash: money(wash1), hydro: hydro1, overhaul: money(overhaul1) },
    ]

  return (
    <>
      <Head>
        <title>{pageTitle}</title>
        <meta charSet="utf-8"/>
        <meta httpEquiv="X-UA-Compatible" content="IE=edge"/>
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta name="description" content={pageDescription}/>
        <meta name="author" content="StringsSG"/>
        <meta property="og:locale" content="en_US" />
        <meta property="og:url" content={PAGE_URL} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={pageTitle}/>
        <meta property="og:description" content={pageDescription} />
        <meta property="article:publisher" content="https://www.facebook.com/StringsSG/" />
        <meta property="article:published_time" content={DATE_PUBLISHED} />
        <meta property="article:modified_time" content={modifiedDate} />
        <meta property="og:image" content={pageImage} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content={pageImage} />
        <meta name="twitter:site" content="@stringssg"/>
        <meta name="twitter:url" content={PAGE_URL}/>
        <meta name="twitter:title" content={pageTitle}/>
        <meta name="twitter:description" content={pageDescription}/>
        <link rel="icon" href="/favicon.ico" />
        <link rel="canonical" href={PAGE_URL}/>
        <script type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(buildAirconServiceGraph({
            path: PAGE_PATH,
            pageName: pageTitle,
            description: pageDescription,
            serviceName: 'Aircon Chemical Hydro Wash Singapore',
            serviceType: 'Aircon Chemical Cleaning',
            // The 1 to 5 unit prices in the table below
            priceRange: `S${hydro1} - S${hydro5}`,
            image: pageImage,
            datePublished: DATE_PUBLISHED,
            modifiedDate,
            breadcrumbName: 'Aircon Chemical Hydro Wash',
          })) }}
        />
      </Head>
      <div>
        <RequestProvider>
            <section id="aircon-hydro-wash-booking" className="relative">
                <div className="-mt-14 relative w-full aspect-21/9 sm:h-200 overflow-hidden">
                    <Image
                        className='bg-linear-to-br from-slate-800 to-orange-300'
                        src={pageImage}
                        fill
                        style={{ objectFit: 'cover' }}
                        alt="Technician rinsing an aircon coil with a water jet during a chemical hydro wash"
                        priority={true}
                        sizes="100vw"
                    />
                </div>
                <div id="campaign" className='relative bg-gray-100 sm:bg-transparent sm:text-white w-full py-2 sm:absolute sm:top-1/2 sm:-translate-y-1/2'>
                    <HydroWashAirconBooking
                        setRequestForm={setRequestForm}
                        setIsQuickBook={setIsQuickBook}
                        setIsCustomBook={setIsCustomBook}
                        setIsShowAirconItemSelection={setIsShowAirconItemSelection}
                        showJobScope={showJobScope}
                        currencyCd={currencyCd}
                        subtitle={`Blower and coil water-jet rinsed, unit stays on the wall. From ${hydro1}, transport included.`}
                    />
                </div>
                {requestForm &&
                    <Services requestType={requestType} requestCategorySettings={requestCategorySettings} setRequestForm={setRequestForm} isQuickBook={isQuickBook} isCustomBook={isCustomBook} asSheet/>
                }
            </section>
            {isShowJobScope &&
                <AnimatePresence>
                    <AirconScope
                        setIsShowJobScope={setIsShowJobScope}
                        serviceType={scopeServiceType}
                        unitType={scopeUnitType} />
                </AnimatePresence>
            }
            {isShowAirconItemSelection &&
                <AnimatePresence>
                    <AirconItemSelection setIsShowAirconItemSelection={setIsShowAirconItemSelection} setRequestForm={setRequestForm} setIsCustomBook={setIsCustomBook} setIsQuickBook={setIsQuickBook} userId={null}/>
                </AnimatePresence>
            }
        </RequestProvider>

        <section className="px-4 py-8 md:py-12">
            <div className="max-w-4xl mx-auto space-y-5 leading-7 md:leading-8 text-gray-700">
                <header className="text-center space-y-2">
                    <h1 className="text-3xl md:text-4xl font-bold text-gray-900">Aircon Chemical Hydro Wash in Singapore</h1>
                    <p className="text-sm text-gray-500">Also called a hydro jet wash · From {hydro1} per unit · {year}</p>
                </header>
                <p>
                    A <strong>chemical hydro wash</strong> is the deep clean between a{' '}
                    <Link href="/sg/aircon-servicing-singapore/aircon-chemical-wash-and-overhaul" prefetch={false} className="text-orange-600 font-semibold hover:underline">chemical wash and a chemical overhaul</Link>.
                    The technician takes out the blower wheel and drain tray, treats the coil and blower with a cleaning chemical and
                    rinses them with a high-pressure water jet. The fan coil stays on the wall, so you get a much cleaner blower than a
                    chemical wash gives, without the time and cost of a full strip-down.
                </p>
                <p>
                    The blower wheel is where dust and mould collect in Singapore&apos;s humidity. When it is dirty, the aircon smells
                    musty, throws black specks out of the vents and moves less air, even right after a general service. Rinsing it
                    out with a water jet is what fixes that.
                </p>
            </div>
        </section>

        <section className="px-4 py-8 md:py-12 bg-gray-50" aria-labelledby="hydro-process">
            <div className="max-w-4xl mx-auto space-y-6">
                <h2 id="hydro-process" className="text-2xl md:text-3xl font-bold text-center uppercase">What a Chemical Hydro Wash Includes</h2>
                <ol className="grid sm:grid-cols-2 gap-4">
                    {STEPS.map((step, i) => (
                        <li key={step.title} className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
                            <p className="font-semibold text-gray-900">{i + 1}. {step.title}</p>
                            <p className="mt-1 text-sm text-gray-600 leading-6">{step.desc}</p>
                        </li>
                    ))}
                </ol>
                <p className="text-sm text-gray-600 text-center">
                    Additional work, such as a gas top-up or replacing a part, is quoted on site and done only with your approval.
                </p>
            </div>
        </section>

        <section id="hydro-wash-price" className="px-4 py-8 md:py-12" aria-labelledby="hydro-price">
            <div className="max-w-4xl mx-auto space-y-5">
                <h2 id="hydro-price" className="text-2xl md:text-3xl font-bold text-center uppercase">Chemical Hydro Wash Price</h2>
                <p className="text-center text-gray-700">
                    Prices for wall-mounted units, transport included and no callout fee. The price per unit drops as you add units.
                </p>
                <div className="max-w-md mx-auto overflow-hidden rounded-xl border border-amber-200 shadow-sm">
                    <table className="w-full text-sm text-gray-700 text-center">
                        <thead className="bg-amber-100 text-amber-800 uppercase">
                            <tr>
                                <th className="p-3 border-b">Units</th>
                                <th className="p-3 border-b">Price</th>
                                <th className="p-3 border-b">Per unit</th>
                            </tr>
                        </thead>
                        <tbody>
                            {hydroServicing.map((row, i) => (
                                <tr key={row.number} className="hover:bg-orange-50">
                                    <td className="p-3 border-b">{row.number}</td>
                                    <td className="p-3 border-b font-semibold text-gray-900">{row.currencyCd} {row.rate}</td>
                                    <td className="p-3 border-b">${(Number(row.rate) / (i + 1)).toFixed(2)}</td>
                                </tr>
                            ))}
                            <tr className="bg-gray-50">
                                <td className="p-3">More than 5</td>
                                <td className="p-3 text-orange-600 font-medium" colSpan={2}>Open a request for pricing</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <p className="text-sm text-gray-600 text-center">
                    Ceiling cassette and ducted units: choose the mount type in the booking sheet and the price updates before you
                    confirm. See all prices on the{' '}
                    <Link href="/sg/aircon-servicing-singapore/aircon-servicing-price-singapore" prefetch={false} className="text-orange-600 font-semibold hover:underline">aircon servicing price page</Link>.
                </p>
            </div>
        </section>

        <section className="px-4 py-8 md:py-12 bg-gray-50" aria-labelledby="hydro-compare">
            <div className="max-w-5xl mx-auto space-y-5">
                <h2 id="hydro-compare" className="text-2xl md:text-3xl font-bold text-center uppercase">Chemical Wash vs Hydro Wash vs Overhaul</h2>
                <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm">
                    <table className="w-full min-w-[36rem] text-sm text-gray-700">
                        <caption className="sr-only">Comparison of chemical wash, chemical hydro wash and chemical overhaul</caption>
                        <thead className="bg-amber-100 text-amber-800">
                            <tr>
                                <th scope="col" className="p-3 text-left"><span className="sr-only">Compare</span></th>
                                <th scope="col" className="p-3 text-left">Chemical wash</th>
                                <th scope="col" className="p-3 text-left bg-amber-200/60">Chemical hydro wash</th>
                                <th scope="col" className="p-3 text-left">Chemical overhaul</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {comparison.map((row) => (
                                <tr key={row.label}>
                                    <th scope="row" className="p-3 text-left font-semibold text-gray-900">{row.label}</th>
                                    <td className="p-3">{row.wash}</td>
                                    <td className="p-3 bg-amber-50 font-medium text-gray-900">{row.hydro}</td>
                                    <td className="p-3">{row.overhaul}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </section>

        <section className="px-4 py-8 md:py-12" aria-labelledby="hydro-signs">
            <div className="max-w-4xl mx-auto space-y-4">
                <h2 id="hydro-signs" className="text-2xl md:text-3xl font-bold text-center uppercase">Signs Your Aircon Needs a Hydro Wash</h2>
                <ul className="max-w-2xl mx-auto space-y-2 text-gray-700">
                    {SIGNS.map((sign) => (
                        <li key={sign} className="flex gap-2"><span aria-hidden="true" className="text-orange-500">✔</span>{sign}</li>
                    ))}
                </ul>
                <p className="max-w-2xl mx-auto text-sm text-gray-600 leading-6">
                    If the unit leaks water or still cools poorly after a recent service, a{' '}
                    <Link href="/sg/aircon-servicing-singapore/aircon-chemical-wash-and-overhaul" prefetch={false} className="text-orange-600 font-semibold hover:underline">chemical overhaul</Link>{' '}
                    is usually the better fix. For water dripping indoors, see{' '}
                    <Link href="/sg/aircon-servicing-singapore/aircon-leaking-water" prefetch={false} className="text-orange-600 font-semibold hover:underline">aircon leaking water</Link>.
                </p>
            </div>
        </section>

        <AirconWhyUs/>
        <AirconCustomerReviews testimonials={testimonials} />
        <AirconBrandsWeSupport/>
        <section className='max-w-5xl mx-auto py-8 px-2' aria-label="Frequently Asked Questions">
            <Faq questionsAnswers={faqs} title="Aircon Chemical Hydro Wash FAQs" />
        </section>
        <BlogsList blogs={airconBlogs} wpPosts={wpPosts} wpNewPosts={wpNewPosts}/>
        <AirconQuickLinks/>
      </div>
      <Script
        id="google-tagmanager-new"
        src="https://www.googletagmanager.com/gtag/js?id=G-QD1HGGHDYJ"
        strategy="lazyOnload"
        onLoad={() => {
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());

          gtag('config', 'G-QD1HGGHDYJ');
        }}
      />
      <Script id="facebook-tracking" strategy="lazyOnload">
        {`!function(f,b,e,v,n,t,s)
          {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
          n.callMethod.apply(n,arguments):n.queue.push(arguments)};
          if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
          n.queue=[];t=b.createElement(e);t.async=!0;
          t.src=v;s=b.getElementsByTagName(e)[0];
          s.parentNode.insertBefore(t,s)}(window, document,'script',
          'https://connect.facebook.net/en_US/fbevents.js');
          fbq('init', '128634041116395');
          fbq('track', 'PageView');`}
      </Script>
    </>
  )
}
